import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, Bell, Settings, Menu, AlertTriangle } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { isSupabaseConfigured } from '../../lib/supabase';
import { NotificationDropdown } from './NotificationDropdown';
import { ThemeToggle } from './ThemeToggle';
import { useSidebarStore } from '../../store/sidebarStore';

export const Header: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, isSupabaseConfigured: authSupabaseConfigured } = useAuthStore();
  const { toggleSidebar } = useSidebarStore();
  const supabaseConfigured = isSupabaseConfigured();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getProfileTypeLabel = (type: string) => {
    const labels = {
      PORTEUR: 'Porteur de projet',
      ACCOMPAGNATEUR: 'Accompagnateur',
      COORDINATEUR: 'Coordinateur CRESS',
      FINANCEUR: 'Financeur',
      ADMIN: 'Administrateur'
    };
    return labels[type as keyof typeof labels] || type;
  };

  const getProfileColor = (type: string) => {
    const colors = {
      PORTEUR: 'bg-green-100 text-green-800',
      ACCOMPAGNATEUR: 'bg-blue-100 text-blue-800',
      COORDINATEUR: 'bg-purple-100 text-purple-800',
      FINANCEUR: 'bg-orange-100 text-orange-800',
      ADMIN: 'bg-red-100 text-red-800'
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  return (
    <header className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 px-6 py-4 transition-all duration-300 sticky top-0 z-40">
      {/* Supabase Configuration Warning */}
      {!supabaseConfigured && (
        <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            <p className="text-orange-800 text-sm font-medium">
              Supabase n'est pas configuré. Cliquez sur "Connect to Supabase" en haut à droite pour configurer la connexion.
            </p>
          </div>
        </div>
      )}
      
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {/* Mobile Menu Button */}
          <button
            onClick={toggleSidebar}
            className="lg:hidden p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200"
          >
            <Menu className="w-5 h-5" />
          </button>
          
          <h1 className="text-2xl font-bold text-blue-600 dark:text-blue-400">CRESS Guadeloupe</h1>
          <span className="hidden md:inline text-sm text-gray-500 dark:text-gray-400">Plateforme d'accompagnement ESS</span>
        </div>

        <div className="flex items-center space-x-4">
          {/* Notifications */}
          {supabaseConfigured && <NotificationDropdown />}

          {/* Theme Toggle */}
          <ThemeToggle />

          {/* Profile Dropdown */}
          <div className="relative group">
            <button className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 hover:scale-105">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 dark:from-blue-500 dark:to-indigo-500 rounded-full flex items-center justify-center shadow-lg">
                  <User className="w-4 h-4 text-white" />
                </div>
                <div className="text-left hidden sm:block">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {user?.full_name || user?.email}
                  </p>
                  {user?.profile_type && (
                    <span className={`text-xs px-2 py-1 rounded-full ${getProfileColor(user.profile_type)}`}>
                      {getProfileTypeLabel(user.profile_type)}
                    </span>
                  )}
                </div>
              </div>
            </button>

            {/* Dropdown Menu */}
            <div className="absolute right-0 mt-2 w-48 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform scale-95 group-hover:scale-100 z-50">
              <div className="py-1">
                <button
                  onClick={() => navigate('/profile')}
                  className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 hover:translate-x-1"
                >
                  <User className="w-4 h-4" />
                  <span>Mon profil</span>
                </button>
                <button
                  onClick={() => navigate('/settings')}
                  className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 hover:translate-x-1"
                >
                  <Settings className="w-4 h-4" />
                  <span>Paramètres</span>
                </button>
                <hr className="my-1 border-gray-200 dark:border-gray-600" />
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200 hover:translate-x-1"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Se déconnecter</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};